import { useState } from "react";
import { Link } from "react-router-dom";
import { money, api, errMsg } from "@/lib/api";
import { cldOptimize } from "@/lib/cloudinary";
import { Package, Leaf, Award, ChevronDown, Plus, ShoppingCart, Star, Bell } from "lucide-react";
import QtyStepper from "@/components/customer/QtyStepper";
import VariantPickerSheet from "@/components/customer/VariantPickerSheet";
import { useStore } from "@/contexts/StoreContext";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

/**
 * ProductCard — Blinkit-quality product tile.
 *
 * • Padded image on top with red-tinted card frame, discount + ETA chips.
 * • Tight info block: name, weight, price with strikethrough MRP.
 * • Variant-aware ADD button:
 *     - No variants  → normal add-to-cart via QtyStepper
 *     - Has variants → tap opens bottom sheet with all pack sizes
 *   Sub-label under the button ("3 options") mirrors Blinkit's pattern.
 */
export default function ProductCard({ p, testId, cardOverride }) {
  const { ui } = useStore();
  const { qtyOf } = useCart() || { qtyOf: () => 0 };
  const { user } = useAuth();
  const nav = useNavigate();
  const [sheet, setSheet] = useState(false);
  const [notified, setNotified] = useState(false);

  // UI customization (SUPER_ADMIN-configured; defaults backed on the server).
  // Section-level `cardOverride` merges over the base — this powers "one
  // section shows +, another ADD, another Buy Now" without touching any
  // other section. Business logic (price/stock/cart) is not affected.
  const card = { ...(ui?.card || {}), ...(cardOverride || {}) };
  const typo = card.typography || {};
  const showDiscountBadge = card.show_discount_badge !== false;
  const showMrp           = card.show_mrp !== false;
  const showDiscountPct   = card.show_discount_pct !== false;
  const showWeight        = card.show_weight !== false;
  // Sprint 58 — delivery time badge OFF by default (user request). Store name never shown.
  const showDeliveryBadge = card.show_delivery_badge === true;
  const showRating        = card.show_rating !== false;
  const showSold          = card.show_sold_count !== false;
  const discountLabelBig  = card.discount_label === "OFF" ? "OFF" : (card.discount_label || "% OFF").replace(/OFF/i, "").trim() || "%";
  const discountLabelSub  = card.discount_label === "%" ? "" : "OFF";
  const deliveryText      = card.delivery_badge_text || "15 MINS";
  const addLabel          = card.add_button_text || "ADD";
  const addVariant        = card.add_button_variant || "text";  // text | plus | cart | text_plus | custom
  const imagePosition     = card.image_position || "center";     // top | center | bottom | custom
  const imageOffsetY      = Math.max(0, Math.min(100, Number(card.image_offset_y ?? 50)));
  const imgObjectPos =
    imagePosition === "top"    ? "center top"    :
    imagePosition === "bottom" ? "center bottom" :
    imagePosition === "custom" ? `center ${imageOffsetY}%` :
                                 "center center";

  // Sprint 55 — extra card/price/button controls (all optional, safe defaults)
  const currency        = card.currency_symbol ?? "₹";
  const currencyBefore  = (card.currency_position || "before") !== "after";
  // Sprint 60 — Blinkit density defaults. Admin overrides still win via `card.*`.
  const nameLines       = Math.max(1, Math.min(3, Number(card.name_lines || 2)));
  const nameSize        = Number(card.name_size || 13);
  const nameWeight      = card.name_weight || 500;
  const priceSize       = Number(card.price_size || 16);
  const priceWeight     = card.price_weight || 700;
  const priceColor      = card.price_color || "";
  const mrpSize         = Number(card.mrp_size || 12);
  const mrpWeight       = card.mrp_weight || 400;
  const mrpColor        = card.mrp_color || "";
  const mrpStrikeW      = Number(card.mrp_strike_width || 1);
  const cardRadius      = Number(card.radius || 14);
  const cardShadow      = card.shadow || "soft"; // none | soft | strong
  const cardPadX        = Number(card.padding_x || 8);
  const cardPadY        = Number(card.padding_y || 8);
  const imageAspect     = card.image_aspect || "4/3"; // wider aspect → image ≈45-50% of card height
  const imagePadding    = Number(card.image_padding ?? 4);
  const btnW            = Number(card.button_width  || 70);
  const btnH            = Number(card.button_height || 32);
  const btnRadius       = Number(card.button_radius || 8);

  // Sprint 57 — Add button colors + shadow + typography
  const addBg           = card.add_bg_color || "";
  const addBorderColor  = card.add_border_color || "";
  const addTextColor    = card.add_text_color || "";
  const addHoverColor   = card.add_hover_color || "";
  const addFontSize     = Number(card.add_font_size || 12);
  const addFontWeight   = card.add_font_weight || 700;
  const addShadow       = card.add_shadow || "none"; // none | soft | strong | glow
  const addPos          = card.add_button_position || "inline"; // inline | bottom-right | image-bottom-right | ...
  const addShadowStyle  = {
    none:   "none",
    soft:   "0 1px 3px rgba(0,0,0,0.08)",
    strong: "0 6px 16px rgba(0,0,0,0.15)",
    glow:   `0 6px 22px -4px ${addBorderColor || "hsl(var(--brand-red) / 0.55)"}`,
  }[addShadow] || "none";
  const addStyle = {
    width: btnW, height: btnH, borderRadius: btnRadius,
    background: addBg || undefined,
    borderColor: addBorderColor || undefined,
    color: addTextColor || undefined,
    boxShadow: addShadowStyle,
    fontSize: `${addFontSize}px`,
    fontWeight: addFontWeight,
    fontFamily: typo.button_font_family || undefined,
    ["--bb-add-hover"]: addHoverColor || undefined,
  };

  // Sprint 57 — Discount badge full customization
  const discBg          = card.discount_bg_color || "";
  const discTextColor   = card.discount_text_color || "";
  const discFontSize    = Number(card.discount_font_size || 11);
  const discFontWeight  = card.discount_font_weight || 800;
  const discRadius      = card.discount_radius;
  const discShape       = card.discount_shape || "rounded"; // rounded | pill | rectangle | circle | tag
  const discWidth       = card.discount_width;
  const discHeight      = card.discount_height;
  const discShadowKey   = card.discount_shadow || "soft";
  const discShadow      = { none: "none", soft: "0 2px 6px rgba(0,0,0,0.12)", strong: "0 6px 14px rgba(0,0,0,0.18)" }[discShadowKey] || "0 2px 6px rgba(0,0,0,0.12)";
  const discShapeRadius = {
    rounded:   discRadius ?? 8,
    pill:      999,
    rectangle: 0,
    circle:    999,
    tag:       discRadius ?? 6,
  }[discShape];
  const discStyle = {
    background: discBg || undefined,
    color: discTextColor || undefined,
    boxShadow: discShadow,
    borderRadius: discShapeRadius,
    width: discWidth || undefined,
    height: discHeight || undefined,
  };

  // Sprint 57 — Delivery badge full customization
  const delBg           = card.delivery_bg_color || "";
  const delTextColor    = card.delivery_text_color || "";
  const delRadius       = card.delivery_radius;
  const delShowIcon     = card.delivery_show_icon !== false;
  const delStyle = {
    background: delBg || undefined,
    color: delTextColor || undefined,
    borderRadius: delRadius != null ? delRadius : undefined,
  };

  const shadowStyle = { none: "none", soft: "0 1px 2px rgba(0,0,0,0.04)", strong: "0 4px 14px rgba(0,0,0,0.08)" }[cardShadow] || "0 1px 2px rgba(0,0,0,0.04)";
  const money2 = (v) => currencyBefore ? `${currency}${Math.round(Number(v || 0))}` : `${Math.round(Number(v || 0))}${currency}`;

  const addNode = (() => {
    if (addVariant === "plus")       return <Plus className="w-4 h-4" />;
    if (addVariant === "cart")       return <ShoppingCart className="w-4 h-4" />;
    if (addVariant === "text_plus")  return (<span className="inline-flex items-center gap-0.5">{addLabel} <Plus className="w-3 h-3" /></span>);
    if (addVariant === "custom")     return addLabel;
    return addLabel;
  })();
  const badgePosCls = {
    "top-left": "top-2 left-2",
    "top-right": "top-2 right-2",
    "bottom-left": "bottom-2 left-2",
    "bottom-right": "bottom-2 right-2",
  }[card.badge_position || "top-left"];
  const deliveryPosCls = {
    "top-left": "top-2 left-2",
    "top-right": "top-2.5 right-2.5",
    "bottom-left": "bottom-2 left-2",
    "bottom-right": "bottom-2 right-2",
    "inside-image": "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
    "outside-image": "-bottom-2 left-1/2 -translate-x-1/2",
  }[card.delivery_badge_position || "top-right"];
  const delFontSize      = Number(card.delivery_font_size || 10);
  const badgeStyleCls = {
    gradient: "bg-gradient-to-b from-primary to-[hsl(var(--brand-red-deep))] text-white",
    solid:    "bg-primary text-white",
    outline:  "bg-white text-primary border border-primary",
  }[card.badge_style || "gradient"];

  const discount = p.mrp && Number(p.mrp) > Number(p.selling_price)
    ? Math.round(((p.mrp - p.selling_price) / p.mrp) * 100)
    : 0;

  const tags = Array.isArray(p.tags) ? p.tags.map((t) => String(t).toLowerCase()) : [];
  const isBestSeller = tags.includes("bestseller") || tags.includes("best-seller") || p.is_best_seller === true;
  const isOrganic = tags.includes("organic") || p.is_organic === true;
  const outOfStock = p.stock === 0 || p.is_active === false;
  const lowStock = !outOfStock && typeof p.stock === "number" && p.stock > 0 && p.stock <= 5;
  const showStock = p.show_stock !== false;
  const hasVariants = !!p.has_variants;

  // Sprint 58 — rating + sold count (derive from safe fallbacks so we never render 0-fake data)
  const rating   = Number(p.rating || p.avg_rating || 0);
  const reviews  = Number(p.review_count || p.reviews_count || 0);
  const soldCnt  = Number(p.sold_count || p.orders_count || 0);
  const hasRating = rating > 0;
  const hasSold   = soldCnt > 0;

  const openVariantSheet = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { nav(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
    setSheet(true);
  };

  // Sprint 58 — Notify Me handler. Uses backend if available, falls back to toast.
  const onNotify = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { nav(`/login?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
    if (notified) { toast.info("You'll be notified when it's back."); return; }
    try {
      await api.post(`/products/${p.id}/notify-me`).catch(() => null);
      setNotified(true);
      toast.success(`We'll notify you when ${p.name} is back in stock`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  // Live variant-line qty from cart — sums all variant lines of this product.
  // Cart lines are variant-scoped, so we approximate visual state by any qty > 0.
  const anyInCart = hasVariants && qtyOf(p.id, null) === 0 &&
    (p.variants || []).some((v) => qtyOf(p.id, v.id) > 0);

  return (
    <>
      <div
        className="group relative bg-white border border-gray-200 hover:border-gray-300 card-lift overflow-hidden flex flex-col h-full"
        style={{ borderRadius: cardRadius, boxShadow: shadowStyle, fontFamily: typo.product_font_family || undefined }}
        data-testid={testId || `product-card-${p.id}`}
      >
        <Link to={`/product/${p.id}`} className="block relative bg-white" style={{ aspectRatio: imageAspect, padding: imagePadding }}>
          <div className={`w-full h-full rounded-xl overflow-hidden bg-[hsl(30_25%_98%)] grid place-items-center transition ${outOfStock ? "opacity-45 grayscale-[0.4]" : ""}`}>
            {p.thumbnail_url
              ? <img src={cldOptimize(p.thumbnail_url, { width: 400 })} alt={p.name} className="w-full h-full object-contain group-hover:scale-[1.05] transition duration-500" style={{ objectPosition: imgObjectPos }} loading="lazy" />
              : <Package className="w-10 h-10 text-primary/30" />}
          </div>

          {showDiscountBadge && discount > 0 && !outOfStock && (
            <div
              className={`absolute ${badgePosCls} overflow-hidden ${discShape === "circle" ? "grid place-items-center" : ""}`}
              style={discStyle}
              data-testid={`discount-${p.id}`}
            >
              <div
                className={`${discBg ? "" : badgeStyleCls} px-2 py-0.5 text-center leading-tight ${discShape === "circle" ? "rounded-full aspect-square grid place-items-center" : ""}`}
                style={{ fontSize: `${discFontSize}px`, fontWeight: discFontWeight, color: discTextColor || undefined, background: discBg || undefined }}
              >
                <div className="tracking-tight" style={{ fontWeight: discFontWeight }}>{discount}{discountLabelBig}</div>
                {discountLabelSub && discShape !== "circle" && (
                  <div className="text-[8px] font-bold uppercase tracking-wider opacity-95 -mt-0.5">{discountLabelSub}</div>
                )}
              </div>
            </div>
          )}

          {showDeliveryBadge && !outOfStock && (
            <div
              className={`absolute ${deliveryPosCls} inline-flex items-center gap-1 bg-white/95 backdrop-blur font-semibold text-foreground px-1.5 py-0.5 shadow-sm`}
              style={{ ...delStyle, fontSize: `${delFontSize}px` }}
            >
              {delShowIcon && <Bell className="w-2.5 h-2.5" style={{ color: delTextColor || undefined }} />}{deliveryText}
            </div>
          )}

          {(isBestSeller || isOrganic) && !outOfStock && (
            <div className="absolute bottom-2 left-2 flex flex-col gap-1 items-start">
              {isBestSeller && <span className="inline-flex items-center gap-0.5 rounded-md bg-amber-500 text-white px-1.5 py-0.5 text-[9px] font-bold font-mono uppercase shadow"><Award className="w-2.5 h-2.5" />Best</span>}
              {isOrganic    && <span className="inline-flex items-center gap-0.5 rounded-md bg-emerald-600 text-white px-1.5 py-0.5 text-[9px] font-bold font-mono uppercase shadow"><Leaf className="w-2.5 h-2.5" />Organic</span>}
            </div>
          )}

          {outOfStock && (
            <div className="absolute inset-0 grid place-items-center pointer-events-none" data-testid={`oos-overlay-${p.id}`}>
              <div className="rounded-lg bg-foreground/90 text-background px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest shadow-lg">
                Out of stock
              </div>
            </div>
          )}
        </Link>

        <div className="flex flex-col flex-1 gap-0.5" style={{ paddingLeft: cardPadX, paddingRight: cardPadX, paddingTop: cardPadY, paddingBottom: cardPadY }}>
          <Link to={`/product/${p.id}`} className="block">
            <h3
              className="font-semibold text-foreground/90"
              style={{
                fontSize: `${nameSize}px`,
                fontWeight: nameWeight,
                lineHeight: 1.2,
                display: "-webkit-box",
                WebkitLineClamp: nameLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                minHeight: `${nameLines * nameSize * 1.2}px`,
              }}
            >{p.name}</h3>
          </Link>
          <div className="text-[10px] text-muted-foreground/90 flex items-center gap-1.5">
            {showWeight && p.weight && <span className="truncate">{p.weight} {p.weight_unit || "g"}</span>}
            {lowStock && showStock && <span className="text-amber-700 font-medium truncate">Only {p.stock} left</span>}
          </div>

          {/* Sprint 58 — rating + sold count strip */}
          {(showRating && hasRating) || (showSold && hasSold) ? (
            <div className="flex items-center gap-2 mt-0.5 text-[10px]" data-testid={`rating-sold-${p.id}`}>
              {showRating && hasRating && (
                <span className="inline-flex items-center gap-0.5 rounded bg-emerald-600 text-white px-1 py-[1px] font-bold tabular-nums leading-none">
                  <Star className="w-2.5 h-2.5 fill-white" />
                  {rating.toFixed(1)}
                  {reviews > 0 && <span className="opacity-90 font-medium ml-0.5">({reviews > 999 ? `${(reviews / 1000).toFixed(1)}k` : reviews})</span>}
                </span>
              )}
              {showSold && hasSold && (
                <span className="text-muted-foreground truncate">
                  {soldCnt > 999 ? `${(soldCnt / 1000).toFixed(1)}k` : soldCnt} sold
                </span>
              )}
            </div>
          ) : null}

          <div className="flex items-end justify-between gap-1 mt-auto pt-1">
            <div className="min-w-0 flex-1 flex flex-col leading-[1.15]">
              <span
                className="tabular-nums truncate"
                style={{ fontSize: `${priceSize}px`, fontWeight: priceWeight, color: priceColor || undefined }}
              >{money2(p.selling_price)}</span>
              {showMrp && showDiscountPct && discount > 0 && (
                <span
                  className="tabular-nums truncate"
                  style={{
                    fontSize: `${mrpSize}px`,
                    fontWeight: mrpWeight,
                    color: mrpColor || "hsl(var(--muted-foreground) / 0.8)",
                    textDecoration: "line-through",
                    textDecorationThickness: `${mrpStrikeW}px`,
                  }}
                >{money2(p.mrp)}</span>
              )}
            </div>
            {outOfStock ? (
              <div className="shrink-0">
                <button
                  onClick={onNotify}
                  className={`inline-flex items-center justify-center gap-1 border font-bold tracking-wide transition press ${notified ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-primary/40 bg-primary/5 text-primary hover:bg-primary hover:text-primary-foreground"}`}
                  style={{ ...addStyle, width: Math.max(btnW, 72) }}
                  data-testid={`notify-me-${p.id}`}
                  title={notified ? "You'll be notified" : "Notify me when back in stock"}
                >
                  <Bell className="w-3 h-3" />
                  <span className="text-[10px]" style={{ fontSize: `${Math.max(9, addFontSize - 2)}px` }}>{notified ? "SET" : "NOTIFY"}</span>
                </button>
              </div>
            ) : (
              <div className="shrink-0">{
              hasVariants ? (
                <button
                  onClick={openVariantSheet}
                  className={`inline-flex flex-col items-center justify-center leading-none border font-bold tracking-wide transition press ${anyInCart ? "bb-stepper-green" : (addBg || addBorderColor || addTextColor ? "border-primary/40 bg-primary/5 text-primary hover:brightness-95" : "bb-add-btn")}`}
                  style={addStyle}
                  data-testid={`variant-open-${p.id}`}
                >
                  <span className="flex items-center gap-0.5" style={{ fontSize: `${addFontSize}px` }}>
                    {addNode} <ChevronDown className="w-2.5 h-2.5" />
                  </span>
                  <span className="font-semibold opacity-80 -mt-0.5" style={{ fontSize: `${Math.max(8, addFontSize - 4)}px` }}>options</span>
                </button>
              ) : (
                <QtyStepper
                  product={p}
                  size="sm"
                  fullWidth
                  testIdPrefix="qty"
                  style={addStyle}
                  addLabel={addNode}
                />
              )
            }</div>
            )}
          </div>
        </div>
      </div>

      {hasVariants && (
        <VariantPickerSheet product={p} open={sheet} onOpenChange={setSheet} />
      )}
    </>
  );
}
